<script setup lang="ts">
import CommonTable from "@/components/common/CommonTable.vue";
import { computed, onMounted, ref, watch } from "vue";
import { ITableHeader } from "@/types/table";
import { useWorkScheduleStore } from "@/features/work-schedule/work-schedule.store";
import {
  IWorkSchedule,
  IWorkScheduleCell,
} from "@/features/work-schedule/work-schedule.types";
import CustomTab from "@/components/custom/CustomTab.vue";
import { ChevronLeft, ChevronRight, Check } from "utruckin-icons";
import { DateTime } from "luxon";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import { ITeam } from "@/features/teams/team.types";
import { IShiftTemplate } from "@/features/shifts/shifts.types";
import { useShiftStore } from "@/features/shifts/shifts.store";
import { useTeamStore } from "@/features/teams/team.store";

const workScheduleStore = useWorkScheduleStore();
const shiftStore = useShiftStore();
const teamStore = useTeamStore();

const schedule = ref<IWorkSchedule | null>(null);
const scheduleLoading = ref(false);
const activeTab = ref<number>(0);
const tabs = [
  { label: "Weekly", value: 0 },
  { label: "Monthly", value: 1 },
];

const withKpi = ref<boolean>(false);

const team = ref<string | null>(null);
const teams = ref<ITeam[]>([]);
const shift = ref<string | null>(null);
const shifts = ref<IShiftTemplate[]>([]);

const currentDate = ref(DateTime.now());
const rangeStart = computed(() => {
  return activeTab.value === 0
    ? currentDate.value.startOf("week")
    : currentDate.value.startOf("month");
});

const rangeEnd = computed(() => {
  return activeTab.value === 0
    ? currentDate.value.endOf("week")
    : currentDate.value.endOf("month");
});

const formattedRange = computed(() => {
  return `${rangeStart.value.toFormat(
    "dd.MM.yyyy"
  )} - ${rangeEnd.value.toFormat("dd.MM.yyyy")}`;
});

const previousWeek = () => {
  currentDate.value =
    activeTab.value === 0
      ? currentDate.value.minus({ weeks: 1 })
      : currentDate.value.minus({ months: 1 });
};

const nextWeek = () => {
  currentDate.value =
    activeTab.value === 0
      ? currentDate.value.plus({ weeks: 1 })
      : currentDate.value.plus({ months: 1 });
};

function toTitleCase(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const scheduleHeaders = computed<ITableHeader[]>(() => {
  const base: ITableHeader[] = [
    { name: "no", label: "No", width: "50px" },
    { name: "name", label: "Username", width: "180px" },
  ];
  const dayCols: ITableHeader[] = (schedule.value?.days || []).map((day) => ({
    name: `day_${day.date}`,
    label: `${DateTime.fromISO(day.date).toFormat("dd.MM")}<br>${toTitleCase(
      day.code
    )}`,
    width: "60px",
    type: "day",
  }));

  const all: ITableHeader[] = [{ name: "all", label: "All", width: "60px" }];
  return [...base, ...dayCols, ...all];
});

const teamItems = computed(() =>
  teams.value.map((t) => ({ value: t.id, name: t.name }))
);

const shiftItems = computed(() =>
  shifts.value.map((s) => ({ value: String(s.id), name: s.title }))
);

const scheduleGroups = computed(() => {
  if (!schedule.value) return [];
  return schedule.value.groups.map((group) => ({
    label: group.shift_name,
    sublabel: group.shift_time,
    count: group.users_count,
    items: group.users.map((user, idx) => {
      const row: Record<string, any> = {
        _idx: idx,
        _userId: user.user_id,
        no: idx + 1,
        name: user.name,
        phone_number: user.phone_number || "—",
        task_count: user.total_tasks,
        task_kpi: user.total_kpi,
      };
      (schedule.value?.days || []).forEach((day) => {
        const cell = user.cells.find((c) => c.date === day.date) || null;
        row[`day_${day.date}`] = cell;
      });
      return row;
    }),
  }));
});

const getCellText = (_cell: IWorkScheduleCell | null): string => {
  return "";
};

const getCellClass = (cell: IWorkScheduleCell | null): string => {
  if (!cell) return "";
  if (cell.is_absent) return "cell--absent";
  if (cell.is_day_off) return "cell--day-off";
  if (cell.worked_count > 0) return "cell--worked";
  return "";
};

onMounted(async () => {
  const shiftLoading = ref(false);
  const teamLoading = ref(false);

  workScheduleStore.customFilter.period = "week";
  workScheduleStore.customFilter.period_date =
    rangeStart.value.toFormat("yyyy-MM-dd");

  schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);

  [shifts.value, teams.value] = await Promise.all([
    shiftStore.getShifts(shiftLoading),
    teamStore.getTeams(teamLoading),
  ]);

  if (workScheduleStore.customFilter.team_id) {
    team.value = workScheduleStore.customFilter.team_id;
  }

  if (workScheduleStore.customFilter.shift_id) {
    shift.value = workScheduleStore.customFilter.shift_id;
  }
});

watch(
  () => activeTab.value,
  async (val) => {
    workScheduleStore.customFilter.period = val == 0 ? "week" : "month";
    workScheduleStore.customFilter.period_date =
      rangeStart.value.toFormat("yyyy-MM-dd");

    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);

watch(
  () => currentDate.value,
  async () => {
    workScheduleStore.customFilter.period =
      activeTab.value == 0 ? "week" : "month";
    workScheduleStore.customFilter.period_date =
      rangeStart.value.toFormat("yyyy-MM-dd");
    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);

watch(
  () => shift.value,
  async (val) => {
    workScheduleStore.customFilter.shift_id = val;
    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);

watch(
  () => team.value,
  async (val) => {
    workScheduleStore.customFilter.team_id = val;
    schedule.value = await workScheduleStore.getWorkSchedule(scheduleLoading);
  }
);
</script>

<template>
  <div class="v-work-schedule">
    <div class="header">
      <CustomTab :tabs="tabs" v-model="activeTab" />

      <div class="header__filter">
        <label class="pill-toggle">
          <span>KPI</span>
          <input type="checkbox" v-model="withKpi" />
          <span class="checkmark" :class="withKpi ? 'active' : ''">
            <Check v-if="withKpi" :size="14" />
          </span>
        </label>

        <CommonDropdown
          v-model="shift"
          :items="shiftItems"
          label="Shift"
          clearable
        />
        <CommonDropdown
          v-model="team"
          :items="teamItems"
          label="Team"
          clearable
        />
        <div class="navigation">
          <div class="left" @click="previousWeek">
            <ChevronLeft :size="20" />
          </div>
          <div class="time">
            {{ formattedRange }}
          </div>
          <div class="right" @click="nextWeek">
            <ChevronRight :size="20" />
          </div>
        </div>
      </div>
    </div>
    <div class="body">
      <CommonTable
        :headers="scheduleHeaders"
        :groups="scheduleGroups"
        :loading="scheduleLoading"
        :sticky-columns="2"
      >
        <template #no="{ index }">
          <span>{{ index + 1 }}</span>
        </template>

        <template
          v-for="day in schedule?.days"
          :key="day.code"
          #[`day_${day.date}`]="{ item }"
        >
          <span :class="['day-cell', getCellClass(item[`day_${day.date}`])]">
            {{ item[`day_${day.date}`].worked_count }}
            <span v-if="withKpi" class="kpi">
              {{ item[`day_${day.date}`].total_kpi }}
            </span>
          </span>
        </template>

        <template #all="{ item }">
          <span>
            {{ item.task_count }}
            <span v-if="withKpi" class="kpi">
              {{ item.task_kpi }}
            </span>
          </span>
        </template>
      </CommonTable>
    </div>
  </div>
</template>

<style scoped lang="scss">
.v-work-schedule {
  padding: var(--base-space-24);
  display: flex;
  flex-direction: column;
  background: var(--color-system-background);
  height: calc(100vh - 80px);
  width: 100%;
  overflow: auto;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__filter {
      display: flex;
      align-items: center;
      gap: var(--base-space-16);

      .navigation {
        border-radius: 14px;
        border: 1px solid var(--color-border);
        display: flex;
        color: var(--color-label);

        .left,
        .right {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 10px 10px;
        }

        .left {
          border-right: 1px solid var(--color-border);
        }

        .time {
          padding: 10px 16px;
          font-weight: 500;
          font-size: 16px;
          line-height: 22px;
          cursor: not-allowed;
          pointer-events: none;
          background: var(--color-secondary-system-background);
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }

        .right {
          border-left: 1px solid var(--color-border);
        }
      }

      .pill-toggle {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
        padding: 9px 12px;
        background: var(--color-secondary-system-background);
        border-radius: 14px;
        font-size: 16px;
        line-height: 24px;
        color: var(--color-label);
        cursor: pointer;
        border: 1px solid var(--color-border);
      }

      .pill-toggle input {
        display: none;
      }

      .checkmark {
        width: 20px;
        height: 20px;
        border: 1px solid var(--color-border);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--color-white);

        &.active {
          border-color: var(--color-primary);
          background: var(--color-primary);
        }
      }
    }
  }

  .day-cell {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 32px;
    border-radius: 4px;
    padding: 12px 14px;
    color: var(--color-label);
    font-size: 14px;
    line-height: 20px;

    &.cell--absent {
      background: var(--color-red-lighter);
    }

    &.cell--day-off {
      background: var(--color-green-light);
    }
  }

  .kpi {
    display: inline-block;
    font-size: 14px;
    line-height: 20px;
    color: var(--color-system-green);
    margin-left: 12px;
  }

  .body {
    display: flex;
  }
}
</style>
